import React, { useState } from 'react';
import { MapPin, X, Calendar, Ticket, Maximize2, Minimize2 } from 'lucide-react';
import { Button } from '../ui/button';

export const StadiumPlanModal = ({ open, onOpenChange, stadium, matchInfo }) => {
  const [isFullscreen, setIsFullscreen] = useState(false);
  
  if (!open || !stadium) return null;

  const handleClose = () => {
    setIsFullscreen(false);
    onOpenChange(false);
  };

  // Mode plein écran - utilise tout l'écran
  if (isFullscreen) {
    return (
      <div className="fixed inset-0 z-50 bg-black/95 flex flex-col" data-testid="stadium-plan-fullscreen">
        {/* Header compact */}
        <div className="flex items-center justify-between px-4 py-3 bg-zinc-900/80 border-b border-zinc-700">
          <div className="flex items-center gap-4">
            <h2 className="text-lg font-bold text-[#C4A052] flex items-center gap-2">
              <MapPin className="h-5 w-5" />
              {stadium.name}
            </h2>
            {matchInfo?.matchNum && (
              <span className="bg-[#C4A052] text-black font-bold px-2 py-0.5 rounded text-sm">
                {matchInfo.matchNum}
              </span>
            )}
            <span className="text-sm text-zinc-300">
              {matchInfo?.eventName || ''} {matchInfo?.date && `• ${matchInfo.date}`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIsFullscreen(false)}
              variant="outline"
              size="sm"
              className="border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/10"
              data-testid="minimize-btn"
            >
              <Minimize2 className="h-4 w-4 mr-1" />
              Réduire
            </Button>
            <Button onClick={handleClose} variant="ghost" size="sm" data-testid="close-fullscreen-btn">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* Image centrée prenant tout l'espace disponible */}
        <div className="flex-1 flex items-center justify-center p-4 overflow-hidden">
          <img
            src={stadium.mapUrl}
            alt={`Plan du stade ${stadium.name}`}
            className="max-w-full max-h-full object-contain"
            data-testid="stadium-plan-image"
          />
        </div>
        
        {/* Footer avec lien */}
        <div className="px-4 py-2 bg-zinc-900/80 border-t border-zinc-700 text-center">
          <a href={stadium.mapUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#C4A052] hover:underline">
            Ouvrir l'image dans un nouvel onglet ↗
          </a>
        </div>
      </div>
    );
  }

  // Mode normal - modal centré avec image GRANDE
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="stadium-plan-modal">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/90" onClick={handleClose} />
      
      {/* Modal Content - Plus large et plus haut */}
      <div className="relative z-10 w-[95vw] max-w-7xl bg-zinc-900 border border-zinc-700 rounded-lg shadow-2xl overflow-hidden">
        
        {/* Header compact */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-700 bg-zinc-900">
          <h2 className="text-lg font-bold text-[#C4A052] flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Plan du Stade
          </h2>
          <div className="flex items-center gap-2">
            <Button
              onClick={() => setIsFullscreen(true)}
              variant="outline"
              size="sm"
              className="border-[#C4A052]/50 text-[#C4A052] hover:bg-[#C4A052]/10"
              data-testid="fullscreen-btn"
            >
              <Maximize2 className="h-4 w-4 mr-1" />
              Plein écran
            </Button>
            <Button onClick={handleClose} variant="ghost" size="sm" data-testid="close-modal-btn">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* Info Bar - Plus compact */}
        <div className="flex items-center justify-between gap-4 mx-4 my-3 bg-zinc-800 rounded-lg px-4 py-2 border border-zinc-700">
          {/* Gauche: Match */}
          <div className="flex items-center gap-3">
            {matchInfo?.matchNum && (
              <span className="bg-[#C4A052] text-black font-bold px-2 py-1 rounded text-sm">
                {matchInfo.matchNum}
              </span>
            )}
            <div>
              <p className="text-sm font-semibold text-white">
                {matchInfo?.eventName || stadium.name}
              </p>
              <p className="text-xs text-zinc-400">
                {matchInfo?.date && (
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="h-3 w-3" />
                    {matchInfo.date}
                  </span>
                )}
                {matchInfo?.categorie && (
                  <span className="ml-3 text-[#C4A052] inline-flex items-center gap-1">
                    <Ticket className="h-3 w-3" />
                    {matchInfo.categorie}
                  </span>
                )}
              </p>
            </div>
          </div>
          
          {/* Droite: Stade */}
          <div className="text-right">
            <p className="text-sm font-semibold text-[#C4A052]">{stadium.name}</p>
            <p className="text-xs text-zinc-400">{stadium.city} • <span className="text-[#C4A052]">{stadium.capacity.toLocaleString()}</span> places</p>
          </div>
        </div>

        {/* Plan - IMAGE GRANDE */}
        <div className="px-4 pb-3 flex items-center justify-center bg-zinc-800 mx-4 rounded-lg border border-zinc-700">
          <img
            src={stadium.mapUrl}
            alt={`Plan du stade ${stadium.name}`}
            className="w-full h-auto max-h-[70vh] object-contain"
            data-testid="stadium-plan-image"
            onError={(e) => {
              console.error('Failed to load stadium image:', stadium.mapUrl);
              e.target.style.display = 'none';
            }}
          />
        </div>

        {/* Footer */}
        <div className="px-4 py-3 text-center border-t border-zinc-700">
          <a href={stadium.mapUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-[#C4A052] hover:underline">
            Ouvrir l'image dans un nouvel onglet ↗
          </a>
        </div>
      </div>
    </div>
  );
};

export default StadiumPlanModal;
